/* History drawer */

import { Fragment, useEffect, useMemo, useState } from 'react';
import { Button, Toggle } from './components.jsx';
import { predictorLabel } from './constants.js';
import { Icon } from './icons.jsx';
import { fmtThousands } from './result.jsx';

// slug -> human label, by field. Built once from the catalogs prop so the
// history cards and compare diff show "Backend-разработчик" instead of
// "backend", "Более 10 лет" instead of "gt10", etc. Falls back to the slug
// itself when the catalog is missing the entry (which would only happen if
// a saved version references a slug that has since been removed from the
// catalog — better to show the slug than to crash).
function buildLabelers(catalogs) {
  if (!catalogs) return null;
  const byList = (list, key = 'slug', name = 'name') => {
    const m = new Map(list.map((x) => [x[key], x[name]]));
    return (slug) => m.get(slug) ?? slug ?? '—';
  };
  const enumByCode = (list) => {
    const m = new Map(list.map((x) => [x.code, x.label]));
    return (code) => m.get(code) ?? code ?? '—';
  };
  const langLabel = enumByCode(catalogs.languages || []);
  const levelLabel = enumByCode(catalogs.languageLevels || []);
  return {
    role: byList(catalogs.roles),
    region: byList(catalogs.regions),
    skill: byList(catalogs.skills),
    experience: enumByCode(catalogs.experience),
    employment: enumByCode(catalogs.employment || []),
    // Renders a [{language, level}, ...] array as a single human string for
    // diff display ("Английский B2, Немецкий A2"); empty list -> '—'.
    languages: (value) => {
      const arr = Array.isArray(value) ? value : [];
      if (!arr.length) return '—';
      return arr.map((e) => `${langLabel(e.language)} ${levelLabel(e.level)}`).join(', ');
    },
  };
}

export function HistoryDrawer({ open, versions, catalogs, onClose, onRestore, onDelete }) {
  const labelers = useMemo(() => buildLabelers(catalogs), [catalogs]);
  const [selected, setSelected] = useState([]);
  const [compareMode, setCompareMode] = useState(false);

  useEffect(() => {
    if (!open) { setSelected([]); setCompareMode(false); }
  }, [open]);

  const toggleSelect = (id) => {
    if (selected.includes(id)) {
      setSelected(selected.filter((x) => x !== id));
    } else if (selected.length < 2) {
      setSelected([...selected, id]);
    } else {
      setSelected([selected[1], id]);
    }
  };

  // Honour the user's pick order: first selected = "from", second = "to".
  // Sign of the delta in CompareBlock follows that direction (negative if
  // the second pick is a smaller vilka than the first).
  const compareData = compareMode && selected.length === 2
    ? selected.map((id) => versions.find((v) => v.id === id))
    : null;

  return (
    <Fragment>
      <div className={'backdrop ' + (open ? 'is-open' : '')} onClick={onClose}></div>
      <aside
        className={'drawer ' + (open ? 'is-open' : '')}
        aria-hidden={!open}
        role="dialog"
        aria-label="История расчётов"
      >
        <div style={{ padding: '20px 24px', borderBottom: '1px solid var(--t-border)', display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <h2 className="t-h2" style={{ margin: 0 }}>История</h2>
          <button
            onClick={onClose}
            aria-label="Закрыть"
            style={{
              width: 36, height: 36, border: 0, background: 'var(--t-bg)', borderRadius: 10,
              cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center'
            }}
          >
            <Icon name="x" size={18} />
          </button>
        </div>

        <div style={{ padding: '16px 24px', borderBottom: '1px solid var(--t-border)' }}>
          <Toggle
            checked={compareMode}
            onChange={(v) => { setCompareMode(v); if (!v) setSelected([]); }}
            label={`Сравнить две версии${selected.length ? ` · выбрано ${selected.length}/2` : ''}`}
          />
        </div>

        <div style={{ flex: 1, overflowY: 'auto', padding: '16px 24px' }}>
          {versions.length === 0 && (
            <div style={{ textAlign: 'center', padding: 48, color: 'var(--t-text-muted)' }}>
              <Icon name="history" size={32} stroke={1.4} />
              <div style={{ marginTop: 12, fontSize: 15 }}>История пуста</div>
              <div style={{ fontSize: 13, marginTop: 4 }}>Сохраните первую версию вилки после расчёта</div>
            </div>
          )}

          <div className="stack-3">
            {versions.map((v, i) => (
              <VersionCard
                key={v.id}
                version={v}
                index={versions.length - i}
                isLatest={i === 0}
                compareMode={compareMode}
                isSelected={selected.includes(v.id)}
                labelers={labelers}
                onSelect={() => toggleSelect(v.id)}
                onRestore={() => onRestore(v)}
                onDelete={() => onDelete(v.id)}
              />
            ))}
          </div>
        </div>

        {compareData && (
          <CompareBlock a={compareData[0]} b={compareData[1]} labelers={labelers} />
        )}
      </aside>
    </Fragment>
  );
}

function VersionCard({ version, index, isLatest, compareMode, isSelected, labelers, onSelect, onRestore, onDelete }) {
  const dt = new Date(version.createdAt);
  const dateStr = dt.toLocaleDateString('ru-RU', { day: 'numeric', month: 'short' }) +
                  ' · ' + dt.toLocaleTimeString('ru-RU', { hour: '2-digit', minute: '2-digit' });

  return (
    <div
      onClick={compareMode ? onSelect : undefined}
      style={{
        background: isSelected ? 'rgba(255,221,45,0.12)' : 'var(--t-bg)',
        border: '2px solid ' + (isSelected ? 'var(--t-yellow)' : 'transparent'),
        borderRadius: 16,
        padding: 16,
        cursor: compareMode ? 'pointer' : 'default',
        transition: 'background 120ms linear, border-color 120ms linear'
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline', marginBottom: 8 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
          <span style={{ fontSize: 13, color: 'var(--t-text-muted)' }}>Версия #{index}</span>
          {isLatest && (
            <span style={{
              padding: '2px 8px', borderRadius: 9999, background: 'var(--t-yellow)',
              fontSize: 11, fontWeight: 600
            }}>текущая</span>
          )}
          {version.result.predictorVersion && (
            <span className="predictor-chip" title={`Расчёт сделан моделью ${version.result.predictorVersion}`}>
              {predictorLabel(version.result.predictorVersion)}
            </span>
          )}
        </div>
        <span style={{ fontSize: 12, color: 'var(--t-text-muted)' }}>{dateStr}</span>
      </div>
      <div className="t-tnum" style={{ fontSize: 22, fontWeight: 700, letterSpacing: '-0.01em' }}>
        {fmtThousands(version.result.min)} — {fmtThousands(version.result.max)} ₽
      </div>
      <div style={{ marginTop: 8 }}>
        <span style={{ fontSize: 13, color: 'var(--t-text-muted)' }}>
          {labelers ? labelers.role(version.form.role) : (version.form.role || 'Без роли')}
          {' · '}
          {labelers ? labelers.experience(version.form.experience) : version.form.experience}
          {' · '}
          {version.form.skills.length} нав.
        </span>
      </div>
      {!compareMode && (
        <div style={{ marginTop: 12, display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 6 }}>
          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); onRestore(); }}>
            Восстановить
          </Button>
          <Button variant="ghost" size="sm" onClick={(e) => { e.stopPropagation(); onDelete(); }}>
            Удалить
          </Button>
        </div>
      )}
    </div>
  );
}

function CompareBlock({ a, b, labelers }) {
  const diffs = [];
  // Each entry: form-key, human title, optional labeler key (from buildLabelers).
  const fields = [
    ['role', 'Роль', 'role'],
    ['experience', 'Опыт', 'experience'],
    ['region', 'Регион', 'region'],
    ['remote', 'Удалёнка', null],
    ['employment', 'Тип занятости', 'employment'],
    ['languages', 'Иностранные языки', 'languages'],
    ['education', 'Образование', null],
  ];
  const present = (slug, kind) => {
    if (slug == null || slug === '') return slug;
    if (kind && labelers && labelers[kind]) return labelers[kind](slug);
    return slug;
  };
  const eq = (av, bv) => JSON.stringify(av) === JSON.stringify(bv);
  fields.forEach(([k, label, kind]) => {
    const av = a.form[k];
    const bv = b.form[k];
    if (!eq(av, bv)) diffs.push({ label, was: present(av, kind), now: present(bv, kind) });
  });
  const skillsAdded = b.form.skills.filter((s) => !a.form.skills.includes(s));
  const skillsRemoved = a.form.skills.filter((s) => !b.form.skills.includes(s));
  if (skillsAdded.length || skillsRemoved.length) {
    const skillName = (slug) => present(slug, 'skill');
    diffs.push({
      label: 'Навыки',
      was: skillsRemoved.length ? '− ' + skillsRemoved.map(skillName).join(', ') : '—',
      now: skillsAdded.length ? '+ ' + skillsAdded.map(skillName).join(', ') : '—'
    });
  }

  const dMid = b.result.mid - a.result.mid;
  // Different predictors can give wildly different numbers for the same
  // resume — warn the user before they read the delta as a real change.
  const aModel = a.result.predictorVersion;
  const bModel = b.result.predictorVersion;
  const differentModels = aModel && bModel && aModel !== bModel;

  return (
    <div style={{ borderTop: '1px solid var(--t-border)', padding: '16px 24px', background: 'var(--t-bg)', maxHeight: '50vh', overflowY: 'auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 12 }}>
        <div className="t-h3" style={{ margin: 0 }}>Что изменилось</div>
        <span className={'delta' + (dMid < 0 ? ' is-down' : '')}>
          <Icon name={dMid > 0 ? 'arrow-up' : 'arrow-down'} size={12} stroke={2.4} />
          {dMid > 0 ? '+' : '−'}{fmtThousands(Math.abs(dMid))} ₽
        </span>
      </div>
      {differentModels && (
        <div className="compare-warning">
          <Icon name="alert" size={16} stroke={2} />
          <span>
            Версии посчитаны разными моделями: <b>{predictorLabel(aModel)}</b> и{' '}
            <b>{predictorLabel(bModel)}</b>. Дельта вилки может отражать различия
            моделей, а не только изменения в резюме.
          </span>
        </div>
      )}
      {diffs.length === 0 ? (
        <div className="t-body t-muted">Содержимое резюме идентично.</div>
      ) : (
        <div className="stack-3">
          {diffs.map((d, i) => (
            <div key={i} style={{ display: 'grid', gridTemplateColumns: '120px 1fr 1fr', gap: 12, fontSize: 14 }}>
              <div className="t-muted">{d.label}</div>
              <div style={{ color: 'var(--t-text-muted)', textDecoration: 'line-through' }}>{formatVal(d.was) || '—'}</div>
              <div style={{ fontWeight: 500 }}>{formatVal(d.now) || '—'}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function formatVal(v) {
  if (v === true) return 'Да';
  if (v === false) return 'Нет';
  if (typeof v === 'string' && v.length > 80) return v.slice(0, 80) + '…';
  return v;
}
